import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, ShieldCheck } from "lucide-react";

const CATEGORIES = ["All", "Pickup & Return", "Refunds", "Booking", "Payments", "Documents", "Cancellation"];

const faqs = [
  {
    id: 1,
    category: "Documents",
    question: "What are the eligibility criteria for renting a car from WOWCARZ?",
    answer: "To rent a vehicle, you must be at least 21 years old and hold a valid, original driving license for Light Motor Vehicles (LMV) that is at least 1 year old at the time of trip commencement. Note: Only physical, original driving licenses are accepted; no digital or alternative formats will be processed.",
  },
  {
    id: 2,
    category: "Pickup & Return",
    question: "Can a friend or family member pick up the car on my behalf if I made the booking?",
    answer: "No. For security and insurance purposes, the vehicle can only be handed over to the individual who made the actual booking. The person picking up the vehicle must match the verified driving license and government ID (e.g., Aadhaar) provided during the booking process.",
  },
  {
    id: 3,
    category: "Documents",
    question: "Where can I find the vehicle's documents?",
    answer: "Physical copies of all necessary vehicle documents are located in the vehicle's dashboard glove compartment. If you require digital copies, please contact our Customer Support team.",
  },
  {
    id: 4,
    category: "Booking",
    question: "What is the minimum booking duration?",
    answer: "The minimum duration for any rental is 18 hours, applicable across both weekdays and weekends.",
  },
  {
    id: 5,
    category: "Pickup & Return",
    question: "Will the vehicle be delivered to my doorstep?",
    answer: "Yes, the vehicle will be dropped at your doorstep.",
  },
  {
    id: 6,
    category: "Booking",
    question: "Can I make changes to my booking?",
    answer: "Yes. Booking modifications can be made with the assistance of our Customer Support team up to 24 hours before your scheduled trip start time. Please note that additional charges may apply if there are changes to the vehicle model or rental duration.",
  },
  {
    id: 7,
    category: "Pickup & Return",
    question: "Can I arrange for the car to be delivered to my home?",
    answer: "Yes, home delivery is available. Standard delivery charges will apply based on the distance to your location.",
  },
  {
    id: 8,
    category: "Pickup & Return",
    question: "Can I book an inter-state drop-off?",
    answer: "No. WOWCARZ does not currently support inter-state drop-off services. Vehicles must be returned to the designated location within the origin state.",
  },
  {
    id: 9,
    category: "Pickup & Return",
    question: "Where should I return the vehicle if the drop location is closed?",
    answer: "WOWCARZ operates 24/7. You can return the vehicle at your scheduled drop-off time without worrying about operational hours.",
  },
  {
    id: 10,
    category: "Pickup & Return",
    question: "What happens if I return the car later than the scheduled drop time?",
    answer: "Late returns will incur additional fees, calculated based on the extra hours utilized and any additional kilometers driven.",
  },
  {
    id: 11,
    category: "Refunds",
    question: "How long does it take to process the security deposit refund?",
    answer: "Security deposits are standardly processed and refunded in 10 banking working days from the date the vehicle is successfully dropped off.",
  },
  {
    id: 12,
    category: "Payments",
    question: "Who is responsible for paying traffic violation charges?",
    answer: "The registered user is entirely responsible for any traffic violations or penalties incurred during the specific date and time of their rental period.",
  },
  {
    id: 13,
    category: "Payments",
    question: "Who covers parking and toll charges?",
    answer: "All parking and toll fees are the sole responsibility of the customer. WOWCARZ does not provide refunds or reimbursements for these expenses.",
  },
  {
    id: 14,
    category: "Payments",
    question: "Do I need to pay fees to cross state borders?",
    answer: "Yes. You are required to pay state border entry taxes/fees in accordance with the local laws of the state you are entering. WOWCARZ will only reimburse these costs if an annual state permit is explicitly purchased for the vehicle.",
  },
  {
    id: 15,
    category: "Payments",
    question: "Is fuel included in the rental cost?",
    answer: "No, fuel is not included in the rental cost. Customers are responsible for all fuel expenses and are required to return the vehicle with the same fuel level it had at the start of the rental period.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const Faqs = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const navRef = useRef(null);

  useEffect(() => {
    const activeEl = navRef.current?.querySelector(`[data-cat="${activeCategory}"]`);
    activeEl?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeCategory]);

  const filtered = activeCategory === "All" ? faqs : faqs.filter((f) => f.category === activeCategory);

  return (
    <section className="min-h-screen bg-transparent pt-20 md:pt-24 text-left">
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 uppercase tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-500 mt-2 text-sm">
            Find answers to common queries about Wowcarz services and policies.
          </p>
        </div>

        <div className="flex gap-8 items-start">
          {/* Sticky side nav — desktop only */}
          <aside
            className="hidden lg:block w-56 flex-shrink-0 sticky top-28 self-start max-h-[calc(100vh-8rem)] overflow-y-auto pb-4 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            ref={navRef}
          >
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Categories</p>
            <nav className="space-y-1 pb-28">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  data-cat={cat}
                  onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
                  className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-all ${
                    activeCategory === cat
                      ? "bg-red-50 text-red-600 font-semibold"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  }`}
                >
                  <span className="leading-tight">{cat}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile category pills */}
            <div className="flex flex-wrap gap-1 mb-6 lg:hidden">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
                  className={`px-[14px] py-[6px] rounded-full border text-xs font-semibold transition-colors ${
                    activeCategory === cat
                      ? "bg-[#1a1917] border-[#1a1917] text-white"
                      : "border-[#ddd5cb] text-[#8c837b] hover:border-[#1a1917] hover:text-[#1a1917]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {filtered.map((faq, index) => (
                <div
                  key={faq.id}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                  >
                    <span className={`flex-1 text-sm font-bold pr-4 leading-snug transition-colors ${openIndex === index ? "text-red-600" : "text-gray-800"}`}>
                      {faq.question}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`shrink-0 text-gray-400 transition-transform duration-200 ${openIndex === index ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openIndex === index && (
                    <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-50">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-10 pt-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2 text-red-600 font-bold text-sm">
                <ShieldCheck size={18} />
                Verified Wowcarz Policy
              </div>
              <p className="text-gray-400 text-xs">
                © {new Date().getFullYear()} Wowcarz. All services are subject to verification.
              </p>
            </div>

            <p className="text-gray-400 text-xs italic pb-28 mt-6">
              Can't find what you're looking for? Contact our support team.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Faqs;
